import * as XLSX from 'xlsx';

// Jest does not resolve this project's absolute import aliases, so this runtime catalog uses a relative path.
// eslint-disable-next-line no-restricted-imports
import {
	firstFileIntraopFields,
	optionalFirstFileIntraopFields,
	requiredFirstFileIntraopFields,
	type FirstFileIntraopField,
	type RequiredFirstFileIntraopField,
} from '../../models/intraopFieldMappings';
import type { FieldMapping } from 'models/models';

export type RequiredIntraopField = RequiredFirstFileIntraopField;
export type FirstFileMappings = Record<FirstFileIntraopField, string>;

export const requiredIntraopFields = requiredFirstFileIntraopFields;
export const optionalIntraopFields = optionalFirstFileIntraopFields;

export interface ParsedFirstFile {
	headers: string[];
	rows: string[][];
	samples: Record<string, string[]>;
}

const normalize = (value: string) =>
	value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();

export async function parseFirstFile(file: File): Promise<ParsedFirstFile> {
	const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
	const worksheetName = workbook.SheetNames.find((name) => workbook.Sheets[name]?.['!ref']);
	if (!worksheetName) throw new Error('The file does not contain a readable worksheet.');

	const matrix = XLSX.utils.sheet_to_json<(string | number | boolean | Date)[]>(workbook.Sheets[worksheetName], {
		header: 1,
		defval: '',
		raw: false,
	});
	const headerIndex = matrix.findIndex((row) => row.some((cell) => String(cell).trim()));
	if (headerIndex < 0) throw new Error('The file does not contain a header row.');

	const headers = matrix[headerIndex].map((cell) => String(cell).trim());
	if (headers.some((header) => !header)) throw new Error('Every populated column must have a header.');
	const normalizedHeaders = headers.map(normalize);
	if (new Set(normalizedHeaders).size !== normalizedHeaders.length)
		throw new Error('The file contains duplicate column headers.');

	const rows = matrix.slice(headerIndex + 1).map((row) => headers.map((_, index) => String(row[index] ?? '').trim()));
	const nonEmptyRows = rows.filter((row) => row.some(Boolean));
	if (!nonEmptyRows.length) throw new Error('The file does not contain any data rows.');

	const samples = Object.fromEntries(
		headers.map((header, index) => [
			header,
			Array.from(new Set(nonEmptyRows.map((row) => row[index]).filter(Boolean))).slice(0, 3),
		])
	);
	return { headers, rows: nonEmptyRows, samples };
}

export function emptyMappings(): FirstFileMappings {
	return Object.fromEntries(firstFileIntraopFields.map((field) => [field.key, ''])) as FirstFileMappings;
}

export function mergeAutoMappings(
	current: FirstFileMappings,
	suggestions: Partial<Record<FirstFileIntraopField, { source_header: string | null; confidence: number }>>,
	manuallyEdited: ReadonlySet<FirstFileIntraopField>,
	minimumConfidence = 0.75
): FirstFileMappings {
	const usedHeaders = new Set(Object.values(current).filter(Boolean));
	const merged = { ...current };
	firstFileIntraopFields.forEach((field) => {
		const suggestion = suggestions[field.key];
		const sourceHeader = suggestion?.source_header;
		if (
			manuallyEdited.has(field.key) ||
			!sourceHeader ||
			(suggestion?.confidence ?? 0) < minimumConfidence ||
			usedHeaders.has(sourceHeader)
		)
			return;
		merged[field.key] = sourceHeader;
		usedHeaders.add(sourceHeader);
	});
	return merged;
}

export function compactMappings(mappings: FirstFileMappings): FieldMapping {
	return Object.fromEntries(Object.entries(mappings).filter(([, header]) => header)) as FieldMapping;
}

export function buildFacilities(
	parsed: ParsedFirstFile,
	mappings: FirstFileMappings
): { name: string; rooms: string[] }[] {
	const facilityIndex = parsed.headers.indexOf(mappings.facility);
	const roomIndex = parsed.headers.indexOf(mappings.room);
	const facilities = new Map<string, { name: string; rooms: Map<string, string> }>();

	parsed.rows.forEach((row) => {
		const facilityName = row[facilityIndex]?.trim();
		if (!facilityName) return;
		const facilityKey = normalize(facilityName);
		if (!facilities.has(facilityKey)) facilities.set(facilityKey, { name: facilityName, rooms: new Map() });
		const roomName = row[roomIndex]?.trim();
		if (roomName) {
			const rooms = facilities.get(facilityKey)?.rooms;
			const roomKey = normalize(roomName);
			if (rooms && !rooms.has(roomKey)) rooms.set(roomKey, roomName);
		}
	});

	return Array.from(facilities.values()).map((facility) => ({
		name: facility.name,
		rooms: Array.from(facility.rooms.values()),
	}));
}
